import { useState } from "react";
import { Sheet } from "./Sheet";
import { Icon } from "./Icon";
import { Spinner } from "./ui";
import { motion } from "motion/react";
import { haptic } from "../telegram/haptics";
import { pressRow } from "../motion";
import { useT } from "../i18n/strings";
import { pick, listSeparator } from "../i18n/labels";
import { fieldStyles as s } from "./fields";

export interface PickerOption {
  id: string;
  fa: string;
  en: string;
  /** Seeded rows come from the catalog; the rest this user added. */
  isSeeded?: boolean;
  count?: number;
}

interface BaseProps {
  title: string;
  options: PickerOption[];
  disabled?: boolean;
  placeholder?: string;
  /** Present only where the blueprint says the list is user-extendable. */
  onAddOption?: (label: string) => Promise<string | void>;
  emptyText?: string;
}

interface SingleProps extends BaseProps {
  value: string | null;
  onChange: (id: string | null) => void;
  clearable?: boolean;
}

export function Picker({
  title,
  options,
  value,
  onChange,
  disabled,
  placeholder,
  onAddOption,
  clearable = true,
  emptyText,
}: SingleProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <>
      <button
        type="button"
        className={s.picker}
        disabled={disabled}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span
          className={[s.pickerValue, !selected && s.pickerEmpty, "bidi"].filter(Boolean).join(" ")}
        >
          {selected ? pick(selected) : (placeholder ?? t.actions.select)}
        </span>
        <Icon name="chevronDown" size={20} className={s.pickerChevron} />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={title}>
        <OptionList
          options={options}
          isSelected={(option) => option.id === value}
          onPick={(option) => {
            onChange(option.id === value && clearable ? null : option.id);
            setOpen(false);
          }}
          emptyText={emptyText}
        />
        {onAddOption && (
          <AddOption
            onAdd={async (label) => {
              const id = await onAddOption(label);
              if (typeof id === "string") onChange(id);
            }}
          />
        )}
      </Sheet>
    </>
  );
}

interface MultiProps extends BaseProps {
  values: string[];
  onChange: (ids: string[]) => void;
}

/** Any-of semantics, matching the `medium` filter and the medium field. */
export function MultiPicker({
  title,
  options,
  values,
  onChange,
  disabled,
  placeholder,
  onAddOption,
  emptyText,
}: MultiProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const selected = options.filter((option) => values.includes(option.id));

  const toggle = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);

  return (
    <>
      <button
        type="button"
        className={s.picker}
        disabled={disabled}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span
          className={[s.pickerValue, !selected.length && s.pickerEmpty].filter(Boolean).join(" ")}
        >
          {selected.length
            ? selected.map((option) => pick(option)).join(listSeparator())
            : (placeholder ?? t.actions.select)}
        </span>
        <Icon name="chevronDown" size={20} className={s.pickerChevron} />
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        footer={
          <button
            type="button"
            className={s.addTrigger}
            onClick={() => setOpen(false)}
            style={{ justifyContent: "center" }}
          >
            {t.actions.done}
          </button>
        }
      >
        <OptionList
          options={options}
          isSelected={(option) => values.includes(option.id)}
          onPick={(option) => toggle(option.id)}
          emptyText={emptyText}
        />
        {onAddOption && (
          <AddOption
            onAdd={async (label) => {
              const id = await onAddOption(label);
              if (typeof id === "string") onChange([...values, id]);
            }}
          />
        )}
      </Sheet>
    </>
  );
}

/* ------------------------------------------------------------ option list -- */

function OptionList({
  options,
  isSelected,
  onPick,
  emptyText,
}: {
  options: PickerOption[];
  isSelected: (option: PickerOption) => boolean;
  onPick: (option: PickerOption) => void;
  emptyText?: string;
}) {
  const t = useT();
  if (!options.length) {
    return <p className={s.hint}>{emptyText ?? t.form.none}</p>;
  }
  return (
    <div className={s.options} role="listbox">
      {options.map((option) => {
        const active = isSelected(option);
        return (
          <motion.button
            key={option.id}
            type="button"
            role="option"
            aria-selected={active}
            className={[s.option, active && s.optionSelected].filter(Boolean).join(" ")}
            onClick={() => {
              haptic.select();
              onPick(option);
            }}
            {...pressRow}
          >
            <span className={s.optionLabels}>
              <span className={s.optionFa}>{pick(option)}</span>
            </span>
            {option.count !== undefined && <span className="mono">{option.count}</span>}
            {option.isSeeded === false && <span className={s.optionCustom}>●</span>}
            {active && <Icon name="check" size={20} className={s.optionCheck} />}
          </motion.button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- add option -- */

function AddOption({ onAdd }: { onAdd: (label: string) => Promise<void> }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const clean = label.trim();
    if (!clean || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(clean);
      haptic.success();
      setLabel("");
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t.errors.title);
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button type="button" className={s.addTrigger} onClick={() => setEditing(true)}>
        <Icon name="add" size={20} />
        {t.actions.addOption}
      </button>
    );
  }

  return (
    <div>
      <div className={s.addRow}>
        <input
          autoFocus
          dir="auto"
          className={`${s.control} ${s.addInput}`}
          value={label}
          placeholder={t.form.newOptionPlaceholder}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && submit()}
        />
        <button
          type="button"
          className={s.addTrigger}
          style={{ inlineSize: "auto" }}
          onClick={submit}
          disabled={busy}
        >
          {busy ? <Spinner size={20} /> : <Icon name="check" size={20} />}
        </button>
      </div>
      {error && <p className={s.error}>{error}</p>}
    </div>
  );
}
